// Video renderer: a neutral "watch page" (player area with the thumbnail, title, channel,
// description) and a "feed / search" view (thumbnail card at small size). Layout cues only, no
// platform logos. The thumbnail is the brief's thumbnail, else the selected hero or social card.
import { useState } from "react";
import { pickMedia, type PreviewDevice, type PreviewModel } from "../../../../../shared/content-preview";
import { formatPreviewDate, initialOf } from "../format";

function Thumb({ model, className }: { model: PreviewModel; className?: string }) {
  const thumb = pickMedia(model, ["thumbnail", "hero", "og", "image"]);
  return (
    <div className={`relative aspect-video w-full overflow-hidden bg-slate-900 ${className ?? ""}`}>
      {thumb ? (
        <img src={thumb.url} alt={thumb.alt} className="h-full w-full object-cover" data-testid="preview-video-thumbnail" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-xs text-slate-300" data-testid="preview-video-thumbnail-missing">Thumbnail goes here</div>
      )}
    </div>
  );
}

function Channel({ model, size = "md" }: { model: PreviewModel; size?: "sm" | "md" }) {
  const box = size === "sm" ? "h-6 w-6 text-[10px]" : "h-10 w-10 text-sm";
  return (
    <div className="flex items-center gap-3">
      {model.identity.logo_url ? (
        <img src={model.identity.logo_url} alt="" className={`${box} rounded-full object-cover`} />
      ) : (
        <span aria-hidden className={`${box} flex items-center justify-center rounded-full bg-slate-700 font-semibold text-white`}>{initialOf(model.identity.name)}</span>
      )}
      <span className="text-sm font-medium text-slate-900" data-testid="preview-video-channel">{model.identity.name}</span>
    </div>
  );
}

export function VideoWatchPage({ model, device }: { model: PreviewModel; device: PreviewDevice }) {
  const [expanded, setExpanded] = useState(false);
  const mobile = device === "mobile";
  const date = formatPreviewDate(model.date);
  return (
    <div className={`min-h-full bg-white ${mobile ? "" : "p-6"}`} data-testid="preview-video-watch">
      <p className="px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">Video watch page (YouTube-style) · preview</p>
      <div className={`mx-auto ${mobile ? "" : "grid max-w-6xl grid-cols-[minmax(0,1fr)_320px] gap-6"}`}>
        <section>
          <div className="relative">
            <Thumb model={model} className={mobile ? "" : "rounded-xl"} />
            <span aria-hidden className="absolute left-1/2 top-1/2 flex h-14 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-2xl bg-black/70 text-2xl text-white">▶</span>
          </div>
          <div className={mobile ? "px-3" : ""}>
            <h1 className="mt-3 text-lg font-semibold text-slate-900 sm:text-xl" data-testid="preview-video-title">{model.title}</h1>
            <div className="mt-3 flex items-center justify-between"><Channel model={model} /></div>
            <div className="mt-3 rounded-xl bg-slate-100 p-3 text-sm text-slate-800">
              <p className="font-medium">— views{date ? ` · ${date}` : ""}</p>
              <p className={`mt-1 whitespace-pre-line ${expanded ? "" : "line-clamp-3"}`} data-testid="preview-video-description">
                {model.text || "No description yet."}
              </p>
              {model.text && (
                <button type="button" onClick={() => setExpanded((value) => !value)} className="mt-1 font-medium text-slate-900">
                  {expanded ? "Show less" : "…more"}
                </button>
              )}
            </div>
          </div>
        </section>
        {!mobile && (
          <aside aria-label="Up next (placeholder)" className="space-y-3">
            {[0, 1, 2].map((index) => (
              <div key={index} className="flex gap-2">
                <div className="aspect-video w-40 shrink-0 rounded-lg bg-slate-200" />
                <div className="flex-1 space-y-2 pt-1"><div className="h-3 w-full rounded bg-slate-200" /><div className="h-3 w-2/3 rounded bg-slate-200" /></div>
              </div>
            ))}
          </aside>
        )}
      </div>
    </div>
  );
}

export function VideoFeedCard({ model, device }: { model: PreviewModel; device: PreviewDevice }) {
  const mobile = device === "mobile";
  return (
    <div className={`min-h-full bg-white ${mobile ? "p-3" : "p-6"}`} data-testid="preview-video-feed">
      <p className="mb-3 text-[11px] font-medium uppercase tracking-wide text-slate-500">Home feed and search results (YouTube-style) · preview</p>
      <div className={mobile ? "space-y-3" : "grid grid-cols-3 gap-4"}>
        <article className="space-y-2 rounded-xl ring-2 ring-amber-400 ring-offset-2" data-testid="preview-video-card">
          <Thumb model={model} className="rounded-xl" />
          <div className="flex gap-2 px-1 pb-1">
            <Channel model={model} size="sm" />
          </div>
          <p className="line-clamp-2 px-1 text-sm font-semibold text-slate-900">{model.title}</p>
          <p className="px-1 pb-2 text-xs text-slate-500">— views · Preview</p>
        </article>
        {!mobile && [0, 1].map((index) => (
          <div key={index} aria-hidden className="space-y-2">
            <div className="aspect-video w-full rounded-xl bg-slate-200" />
            <div className="h-3 w-5/6 rounded bg-slate-200" /><div className="h-3 w-1/2 rounded bg-slate-200" />
          </div>
        ))}
      </div>
      <div className="mt-6 flex gap-3" data-testid="preview-video-search-row">
        <Thumb model={model} className={`shrink-0 rounded-xl ${mobile ? "!w-40" : "!w-80"}`} />
        <div className="min-w-0 space-y-1">
          <p className="line-clamp-2 text-sm font-semibold text-slate-900 sm:text-base">{model.title}</p>
          <p className="text-xs text-slate-500">— views · Preview</p>
          <Channel model={model} size="sm" />
          {!mobile && <p className="line-clamp-2 text-xs text-slate-600">{model.description}</p>}
        </div>
      </div>
    </div>
  );
}
